

























































































































import mixins from 'vue-typed-mixins'
import { mapGetters } from 'vuex'
import Section from '@/builder/sections/mixins/Section'
import TimeZoneText from '@/builder/sections/shared/TimeZoneText.vue'
import { getRgbaColor } from '@/lib/calendesk-js-library/filters/getRgbaColor'
import { RoundType } from '@/calendesk/models/RoundType'
import { EmployeeSelectionModeType } from '@/calendesk/models/EmployeeSelectionModeType'
import {
  DATE_TITLE_KEY,
  previewStepForConfigurationKey,
  SelectionStepKey,
  stepPlaceholderFromTitle,
  TITLE_BY_STEP
} from '@/builder/sections/section/calendars/calendarV3Preview'

export default mixins(Section).extend({
  name: 'CalendarV3',
  components: {
    TimeZoneText
  },
  computed: {
    ...mapGetters({
      editedConfigurationKey: 'builder/getEditedConfigurationKey'
    }),
    previewStepKey (): SelectionStepKey | 'date' {
      return previewStepForConfigurationKey(this.editedConfigurationKey, this.areEmployeesHidden)
    },
    previewStepTitle (): string {
      return this.data.configuration[TITLE_BY_STEP[this.previewStepKey as SelectionStepKey]]
    },
    previewOptions (): Array<Record<string, any>> {
      if (this.previewStepKey === 'service') {
        return [{
          icon: null,
          title: this.$trans('wb_dummy_text_service'),
          subtitle: `60 ${this.$trans('minutes')}`,
          price: 10000
        }]
      }

      if (this.previewStepKey === 'location') {
        return [{
          icon: '$marker',
          title: this.$trans('wb_dummy_text_address'),
          subtitle: null,
          price: null
        }]
      }

      const person = {
        icon: null,
        title: this.$trans('employee_example_fullname'),
        subtitle: this.$trans('role_manager'),
        price: null
      }

      // "Any person" heads the list unless the section only offers named people.
      return this.employeeSelectionMode === EmployeeSelectionModeType.SPECIFIC_ONLY
        ? [person]
        : [{ icon: null, title: this.$trans('any_person_label'), subtitle: null, price: null }, person]
    },
    primaryColor (): string {
      return this.draftConfiguration.wb_color_primary__color__
    },
    v3Radius (): { widget: string; element: string } {
      // Mirrors the Vuetify rounded-* scale that classForViewRound applies to v-cards.
      switch (this.viewRoundType) {
        case RoundType.ZERO:
          return { widget: '0', element: '0' }
        case RoundType.SM:
          return { widget: '2px', element: '2px' }
        case RoundType.LG:
          return { widget: '8px', element: '8px' }
        case RoundType.XL:
          return { widget: '16px', element: '12px' }
        case RoundType.PILL:
        case RoundType.CIRCLE:
          return { widget: '24px', element: '9999px' }
        default:
          return { widget: '4px', element: '4px' }
      }
    },
    widgetStyleVars (): Record<string, string> {
      const textBase = this.textColor1 || this.primaryColor
      const sectionHeight = Number(this.data.configuration.wb_height__style_height__) || 700

      return {
        '--v3-surface': this.backgroundColor1,
        '--v3-p04': getRgbaColor(this.primaryColor, 0.04),
        '--v3-p08': getRgbaColor(this.primaryColor, 0.08),
        '--v3-border-soft': getRgbaColor(textBase, 0.12),
        '--v3-radius-widget': this.v3Radius.widget,
        '--v3-radius': this.v3Radius.element,
        '--v3-max-height': `${Math.max(480, sectionHeight - 60)}px`,
        '--v3-shadow-widget': this.data.configuration.wb_view_flat__checkbox__
          ? 'none'
          : `0 2px 6px ${getRgbaColor(textBase, 0.05)}, 0 12px 32px ${getRgbaColor(textBase, 0.09)}`,
        '--v3-shadow-card': `0 0 0 1px ${getRgbaColor(textBase, 0.08)}, 0 1px 2px ${getRgbaColor(textBase, 0.07)}`
      }
    },
    railSteps (): Array<Record<string, any>> {
      const configured = this.data.configuration.wb_selection_order__select__?.value as string | undefined
      const order = (configured || 'service_location_employee').split('_') as Array<SelectionStepKey>

      const labelByKey: Record<SelectionStepKey, string> = {
        service: this.$trans('service'),
        location: this.$trans('service_location'),
        employee: this.$trans('employee')
      }

      const valueByKey: Record<string, string> = {
        service: this.$trans('wb_dummy_text_service'),
        location: this.$trans('wb_dummy_text_address'),
        // The auto-assign mode pre-fills the step with "any person"
        // on the client; the rail preview mirrors that resolved value.
        employee: this.employeeSelectionMode === EmployeeSelectionModeType.AUTO_ASSIGN
          ? this.$trans('any_person_label')
          : this.$trans('wb_dummy_text_reviews_full_name')
      }

      const steps = order
        .filter((key: SelectionStepKey) => labelByKey[key] && !(key === 'employee' && this.areEmployeesHidden))
        .map((key: SelectionStepKey) => ({
          key,
          label: labelByKey[key],
          placeholder: this.stepPlaceholder(TITLE_BY_STEP[key])
        }))

      steps.push({
        key: 'date' as SelectionStepKey,
        label: this.$trans('date'),
        placeholder: this.stepPlaceholder(DATE_TITLE_KEY)
      })

      // Everything before the previewed step reads as already chosen and
      // everything after it as still to come - the rail a visitor sees at the
      // moment they reach the step whose wording is being edited.
      const currentIndex = steps.findIndex((step) => step.key === this.previewStepKey)

      return steps.map((step, index: number) => ({
        ...step,
        position: index + 1,
        done: index < currentIndex,
        current: index === currentIndex,
        value: index < currentIndex ? valueByKey[step.key] : null
      }))
    },
    getMonthLabel (): string {
      return this.$moment().format('MMMM YYYY')
    },
    getWeekdayLabels (): Array<string> {
      const labels = []
      const monday = this.$moment().startOf('isoWeek')

      for (let i = 0; i < 7; i++) {
        labels.push(monday.clone().add(i, 'days').format('dd'))
      }

      return labels
    },
    getGridOffset (): number {
      return this.$moment().startOf('month').isoWeekday() - 1
    },
    getGridDays (): Array<Record<string, any>> {
      const days = []
      const today = this.$moment().date()
      const daysInMonth = this.$moment().daysInMonth()

      for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber++) {
        const isPast = dayNumber < today
        days.push({
          dayNumber,
          disabled: isPast || dayNumber % 7 === 0,
          selected: dayNumber === Math.min(today + 2, daysInMonth)
        })
      }

      return days
    },
    getSelectedDayLabel (): string {
      const daysInMonth = this.$moment().daysInMonth()
      const selectedDay = Math.min(this.$moment().date() + 2, daysInMonth)
      return this.$moment().date(selectedDay).format('dddd, D MMMM')
    },
    previewSlots (): Array<string> {
      return ['09:00', '10:30', '12:15', '14:30', '16:00', '17:30']
    }
  },
  methods: {
    stepPlaceholder (configurationKey: string): string {
      return stepPlaceholderFromTitle(this.data.configuration[configurationKey])
    }
  }
})
